import { categoryConfig } from "@/lib/categoryConfig";
import { reviewSections } from "@/lib/reviewSectionConfig";
import { adviceSections } from "@/lib/adviceSectionConfig";
import { productHubs } from "@/lib/productHubConfig";
import { categoryAliases, mainCategoryAliases } from "@/lib/categoryAliases";
import { brandConfig } from "@/lib/brandConfig";
import { productCategories } from "@/lib/productCategoryConfig";

const normaliseLabel = (value: string) => value.toLowerCase().trim().replace(/&amp;/g, "&").replace(/\s+/g, " ");
const labelSlug = (value: string) => normaliseLabel(value).replace(/\s+/g, "-").replace(/-+$/, "");

// Imported editorial labels are not URL slugs. Only emit destinations defined
// by the navigation taxonomy; compound labels prefer their specific topic.
const editorialCategoryPaths: Record<string, string> = {
  news: "/articles", "garden-news": "/articles", events: "/articles",
  "chelsea-garden-show": "/articles", uncategorized: "/articles", articles: "/articles",
  gardening: "/category/seasonal-guide", "by-month": "/category/seasonal-guide",
  "garden-life": "/category/how-to?sub=troubleshooting",
  "garden-tips": "/category/how-to?sub=troubleshooting",
  "garden-troubleshooting": "/category/how-to?sub=troubleshooting",
  "garden-tips,-pruning-&-training": "/category/how-to?sub=pruning",
  "lawn-care": "/category/advice?sub=lawns",
  "gardening-problems": "/advice/problem-solving",
  "pest-&-problem-solving": "/advice/problem-solving",
  "wildlife-&-bird-feeding": "/bird-care", "wildlife-gardening": "/category/plants?sub=wildlife-plants",
  "ponds-&-water-features": "/category/advice?sub=ponds",
  "flower-guides": "/category/flowers", "growing-guides": "/category/grow-guides",
  allotment: "/category/grow-guides", "growing-vegetables": "/category/grow-guides",
  "vegetables-growing": "/category/grow-guides", "winter-gardening": "/category/how-to?sub=winter",
  "feeding-&-watering": "/category/advice?sub=feeding-watering",
  "gardening-books-&-gifts": "/reviews/hand-tools", "pots-&-planters": "/planters-pots",
  "gazebos,-parasols-&-patio-furniture": "/reviews/outdoor-furniture",
  "fire-pits-&-patio-heaters": "/reviews/heating-cooling",
  "bbqs-&-outdoor-cooking": "/bbqs-outdoor-cooking", "outdoor-living": "/reviews/outdoor-furniture",
  "battery-power-packs": "/reviews/power-tools", "battery-power": "/reviews/power-tools",
  "power-stations": "/reviews/power-tools", "solar-power": "/reviews/power-tools",
  "inverter-generators": "/generators/category/inverter-generators",
  tillers: "/tillers-cultivators", "grass-strimmers": "/strimmers",
  "air-conditioning": "/air-conditioning-coolers", "lawn-mower-reviews": "/lawn-mowers",
  review: "/category/reviews", "hanging-baskets": "/planters-pots",
};

function findCategoryPath(value: string): string | null {
  const slug = labelSlug(value);
  if (editorialCategoryPaths[slug]) return editorialCategoryPaths[slug];
  if (categoryRedirects[slug]) return categoryRedirects[slug];
  if (categoryConfig[slug]) return `/category/${slug}`;
  if (productHubs[slug]) return `/${slug}`;
  if (reviewSections[slug]) return `/reviews/${slug}`;
  if (adviceSections[slug]) return `/advice/${slug}`;
  if (brandConfig[slug]) return `/brand/${slug}`;
  for (const [parent, config] of Object.entries(categoryConfig)) {
    if ([config.title, ...(mainCategoryAliases[parent] || [])].some(name => labelSlug(name) === slug)) {
      return `/category/${parent}`;
    }
    for (const section of config.sections) {
      for (const link of section.links) {
        if ([link.slug, link.name, ...(categoryAliases[link.slug] || [])].some(name => labelSlug(name) === slug)) {
          return link.href || `/category/${parent}?sub=${link.slug}`;
        }
      }
    }
  }
  // Product-category listings (/products/:slug) as a last resort so an editorial
  // category that has its own listing stops falling back to the article index.
  // Only used when the config key equals its slug, matching the sitemap rule:
  // a mismatched pair renders a page whose canonical points elsewhere.
  const productCat = productCategories[slug];
  if (productCat && productCat.slug === slug) return `/products/${slug}`;
  return null;
}

/** A validated destination for an editorial category name, not a guessed URL. */
export function getCategoryPath(label: string): string | null {
  const direct = findCategoryPath(label);
  if (direct) return direct;
  const parts = label.split(",").map(part => part.trim().replace(/^-+|-+$/g, "")).filter(Boolean);
  if (parts.length > 0 && label.includes(",")) {
    for (const part of parts) {
      const target = findCategoryPath(part);
      if (target && target !== "/articles") return target;
    }
    if (parts.some(part => findCategoryPath(part) === "/articles")) return "/articles";
  }
  return null;
}

// Redirect map for legacy/moved category slugs
export const categoryRedirects: Record<string, string> = {
  "outdoor-furniture-review": "/category/reviews?sub=garden-chairs",
  "buying-guides": "/category/reviews",
  "reviews-and-buying-guides": "/category/reviews",
  "reviews-buying-guides": "/category/reviews",
  // Short/common aliases pointing at the correct review hub
  "watering": "/reviews/watering-irrigation",
  "irrigation": "/reviews/watering-irrigation",
  "bbq": "/reviews/bbq-outdoor-cooking",
  "bbqs": "/reviews/bbq-outdoor-cooking",
  "furniture": "/reviews/outdoor-furniture",
  "outdoor-furniture": "/reviews/outdoor-furniture",
  "power-tools": "/reviews/power-tools",
  "hand-tools": "/reviews/hand-tools",
  "garden-machinery": "/reviews/garden-machinery",
  "heating": "/reviews/heating-cooling",
  "cooling": "/reviews/heating-cooling",
  "plants": "/reviews/plants-growing",
  "growing": "/reviews/plants-growing",
  "pets": "/reviews/pets-wildlife",
  "wildlife": "/reviews/pets-wildlife",
  "play": "/reviews/play-leisure",
  "leisure": "/reviews/play-leisure",
  "workshop": "/reviews/workshop-safety",
  "safety": "/reviews/workshop-safety",
  "brands": "/reviews/brands",
};

// Legacy subcategory slugs (?sub=...) under a given parent category.
export const subcategoryRedirects: Record<string, Record<string, string>> = {
  advice: {
    "buying-guides-lawn-mowers": "/best-lawn-mowers-uk",
    "buying-guides-chainsaws": "/best-chainsaws-uk",
    "buying-guides-generators": "/best-generators-uk",
    "buying-guides-pressure-washers": "/best-pressure-washers-uk",
  },
};

/**
 * Resolve the canonical destination for a /category/:slug URL, or null when the
 * slug is a real category page (or genuinely unknown).
 *
 * Shared by the route's server-side `beforeLoad` (issues a real 301 so crawlers
 * never see the client shell) and the page component (safety net for
 * client-side navigations).
 */
export function resolveCategoryRedirect(
  categorySlug?: string | null,
  subcategorySlug?: string | null,
): string | null {
  if (!categorySlug) return null;

  if (subcategorySlug) {
    const target = subcategoryRedirects[categorySlug]?.[subcategorySlug];
    if (target) return target;
  } else if (categoryRedirects[categorySlug]) {
    return categoryRedirects[categorySlug];
  }

  if (categoryConfig[categorySlug]) return null;

  const editorialTarget = getCategoryPath(categorySlug);
  if (editorialTarget && editorialTarget !== `/category/${categorySlug}`) return editorialTarget;

  // Nested subcategory of a known top-level menu
  for (const [parentSlug, parentConfig] of Object.entries(categoryConfig)) {
    const link = parentConfig.sections.flatMap(s => s.links).find(l => l.slug === categorySlug);
    if (link) {
      return link.href || `/category/${parentSlug}?sub=${categorySlug}`;
    }
  }
  if (reviewSections[categorySlug]) return `/reviews/${categorySlug}`;
  if (adviceSections[categorySlug]) return `/advice/${categorySlug}`;
  if (productHubs[categorySlug]) return `/${categorySlug}`;

  return null;
}
